import React from 'react';
import { Link } from 'react-router-dom';
import { T } from '../translations';

// ─── Design tokens (match App.js) ───────────────────────────────────────────
const C = {
  navy:       '#1B2437',
  navyMid:    '#223049',
  orange:     '#F59420',
  white:      '#ffffff',
  off:        '#f8f9fa',
  border:     '#e5e7eb',
  textDark:   '#111827',
  text:       '#374151',
  muted:      '#6b7280',
  success:    '#22c55e',
  r:          '8px',
};

const IconCheck = ({ size = 16, color = C.success }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M2 8l4 4 8-8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconGlobe = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <circle cx="14" cy="14" r="11" stroke={C.orange} strokeWidth="2" />
    <ellipse cx="14" cy="14" rx="5" ry="11" stroke={C.orange} strokeWidth="1.5" />
    <path d="M3 14h22M4 9h20M4 19h20" stroke={C.orange} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconHandshake = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path d="M2 18l6-6 4 4 6-6 8 6" stroke={C.orange} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 16l2 2" stroke={C.orange} strokeWidth="2" strokeLinecap="round" />
    <circle cx="14" cy="10" r="3" stroke={C.orange} strokeWidth="2" />
  </svg>
);

const IconShield = ({ color = C.orange }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path d="M14 3L4 7v7c0 5.5 4.3 10.7 10 12 5.7-1.3 10-6.5 10-12V7L14 3z" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    <path d="M9 14l3.5 3.5L19 11" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconArrow = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function HomePage({ lang = 'es' }) {
  const t = T[lang];

  const btnOrange = {
    background: C.orange, color: C.white, border: 'none',
    padding: '13px 28px', borderRadius: C.r,
    fontWeight: 700, fontSize: 15, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 8,
    textDecoration: 'none', transition: 'background 0.2s',
  };

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, 'Segoe UI', sans-serif", color: C.text, background: C.white }}>
      {/* ════════════════════════════════════════════════════ HERO — two path cards */}
      <section id="top" style={{
        padding: '88px 48px 72px',
        background: C.off,
        minHeight: '60vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto', width: '100%' }}>
          <h1 style={{
            fontSize: 36, fontWeight: 800, color: C.textDark,
            letterSpacing: -1.2, lineHeight: 1.2, marginBottom: 48, textAlign: 'center',
          }}>
            {t.home_hero_h1}
          </h1>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
            {/* Card 1 — Global Care */}
            <Link
              to="/globalcare"
              style={{
                display: 'flex', flexDirection: 'column',
                background: C.navy, borderRadius: 16, padding: '40px 36px',
                textDecoration: 'none', color: C.white,
                border: `3px solid ${C.navy}`,
                transition: 'box-shadow 0.2s, transform 0.2s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(27,36,55,0.35)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'none';
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: C.orange, marginBottom: 16 }}>
                LPC Global Care
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.25, marginBottom: 12, color: C.white }}>
                {t.home_card1_title}
              </h2>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, marginBottom: 24, flex: 1 }}>
                {t.home_card1_desc}
              </p>
              <span style={{
                ...btnOrange, alignSelf: 'flex-start',
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}>
                {t.home_card_cta} <IconArrow />
              </span>
            </Link>

            {/* Card 2 — Pasantías */}
            <Link
              to="/pasantias"
              style={{
                display: 'flex', flexDirection: 'column',
                background: C.navyMid, borderRadius: 16, padding: '40px 36px',
                textDecoration: 'none', color: C.white,
                border: `3px solid ${C.navyMid}`,
                transition: 'box-shadow 0.2s, transform 0.2s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(34,48,73,0.35)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'none';
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: C.orange, marginBottom: 16 }}>
                LPC Pasantías
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.25, marginBottom: 12, color: C.white }}>
                {t.home_card2_title}
              </h2>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, marginBottom: 24, flex: 1 }}>
                {t.home_card2_desc}
              </p>
              <span style={{
                ...btnOrange, alignSelf: 'flex-start',
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}>
                {t.home_card_cta} <IconArrow />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════ COMPARISON TABLE */}
      <section className="section-pad" style={{ padding: '88px 48px', background: C.white }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: 2.5, textTransform: 'uppercase', color: C.orange, fontWeight: 700, marginBottom: 10 }}>
            {t.cmp_eyebrow}
          </div>
          <h2 className="section-h2" style={{
            fontSize: 40, fontWeight: 800, color: C.textDark,
            letterSpacing: -1.2, lineHeight: 1.15, marginBottom: 48,
          }}>
            {t.cmp_h2}
          </h2>
          <div style={{ overflowX: 'auto', borderRadius: 12, boxShadow: '0 2px 20px rgba(0,0,0,0.07)', border: `1px solid ${C.border}` }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: C.white, minWidth: 600 }}>
              <thead>
                <tr>
                  <th style={{ background: C.navy, color: 'rgba(255,255,255,0.35)', fontSize: 13, fontWeight: 600, padding: '18px 24px', textAlign: 'left' }}></th>
                  <th style={{ background: C.navy, color: C.white, fontSize: 13, fontWeight: 700, padding: '18px 24px', textAlign: 'left', borderBottom: `3px solid ${C.orange}` }}>
                    LPC Global Care
                  </th>
                  <th style={{ background: C.navy, color: C.white, fontSize: 13, fontWeight: 700, padding: '18px 24px', textAlign: 'left', borderBottom: `3px solid rgba(245,148,32,0.4)` }}>
                    LPC Pasantías
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  [t.cmp_who,        t.cmp_who_care,    t.cmp_who_intern],
                  [t.cmp_lang,       t.cmp_lang_care,   t.cmp_lang_intern],
                  [t.cmp_dur,        t.cmp_dur_care,    t.cmp_dur_intern],
                  [t.cmp_pay,        t.cmp_pay_care,    t.cmp_pay_intern],
                  [t.cmp_lang_train, <span style={{ color: C.muted }}>{t.cmp_lang_orient}</span>, <span style={{ color: C.muted }}>—</span>],
                  [t.cmp_big,        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><IconCheck />{t.cmp_yes_full}</span>, <span style={{ color: C.muted }}>— {t.cmp_na}</span>],
                  [t.cmp_support,    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><IconCheck />{t.cmp_az}</span>, <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><IconCheck />{t.cmp_az}</span>],
                ].map(([label, care, intern], i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? C.white : C.off }}>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: C.muted, fontWeight: 500, borderBottom: `1px solid ${C.border}` }}>{label}</td>
                    <td style={{ padding: '14px 24px', fontSize: 14, color: C.text, borderBottom: `1px solid ${C.border}` }}>{care}</td>
                    <td style={{ padding: '14px 24px', fontSize: 14, color: C.text, borderBottom: `1px solid ${C.border}` }}>{intern}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════ TRUST STRIP */}
      <section style={{ background: C.off, padding: '56px 48px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 32 }} className="trust-grid">
          {[
            { num: null,   icon: <IconGlobe />,    headline: t.trust1, sub: t.trust1_sub },
            { num: null,   icon: <IconHandshake />, headline: t.trust2, sub: t.trust2_sub },
            { num: null,   icon: <IconShield />,    headline: t.trust3, sub: t.trust3_sub },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, flexShrink: 0, background: C.white, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                {item.icon}
              </div>
              <div>
                {item.num && <div style={{ fontSize: 36, fontWeight: 900, color: C.orange, lineHeight: 1, letterSpacing: -1, marginBottom: 4 }}>{item.num}</div>}
                <div style={{ fontSize: 15, fontWeight: 700, color: C.textDark, lineHeight: 1.3 }}>{item.headline}</div>
                {item.sub && <div style={{ fontSize: 13, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>{item.sub}</div>}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
